import { vectorizeImageBitmapOrCanvas, type VectorizeOptions } from './vtracer';
import { apiBase, withApiKey } from '../apiAuth';

export type VectorizeTarget = {
  elementId: string;
  bbox: { x: number; y: number; width: number; height: number };
  /** Source image URL (usually /api/templates/{id}-source.png) */
  src?: string;
  sourceUrl?: string;
  uniqueId?: string;
  kind?: string;
};

export type OrnamentSvgResult = {
  elementId: string;
  svg: string;
  bbox: VectorizeTarget['bbox'];
  ok: true;
} | {
  elementId: string;
  bbox: VectorizeTarget['bbox'];
  ok: false;
  error: string;
};

function resolveUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl) || pathOrUrl.startsWith('data:')) {
    return pathOrUrl.includes('/api/') ? withApiKey(pathOrUrl) : pathOrUrl;
  }
  const base = apiBase() || (typeof window !== 'undefined' ? window.location.origin : '');
  const abs = pathOrUrl.startsWith('/') ? `${base}${pathOrUrl}` : `${base}/${pathOrUrl}`;
  return abs.includes('/api/') ? withApiKey(abs) : abs;
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function cropToCanvas(
  img: HTMLImageElement | ImageBitmap,
  bbox: { x: number; y: number; width: number; height: number },
): HTMLCanvasElement {
  const sx = Math.max(0, Math.floor(bbox.x));
  const sy = Math.max(0, Math.floor(bbox.y));
  const sw = Math.max(1, Math.floor(bbox.width));
  const sh = Math.max(1, Math.floor(bbox.height));
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.drawImage(img as CanvasImageSource, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas;
}

export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(svg)))}`;
}

/**
 * Given vectorizeTargets + source image URL, crop each bbox and run VTracer.
 * Failures are soft (ok:false) so raster crops can remain.
 */
export async function ornamentsFromTemplate(
  targets: VectorizeTarget[],
  sourceImageUrl: string,
  opts?: VectorizeOptions & { onProgress?: (done: number, total: number, elementId: string) => void },
): Promise<OrnamentSvgResult[]> {
  if (!targets.length) return [];
  const url = resolveUrl(sourceImageUrl);
  const img = await loadImage(url);
  const { onProgress, ...vectorizeOpts } = opts || {};
  const results: OrnamentSvgResult[] = [];
  let done = 0;
  for (const t of targets) {
    try {
      const crop = cropToCanvas(img, t.bbox);
      const svg = await vectorizeImageBitmapOrCanvas(crop, vectorizeOpts);
      results.push({ elementId: t.elementId, svg, bbox: t.bbox, ok: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn('VTracer failed for', t.elementId, message);
      results.push({ elementId: t.elementId, bbox: t.bbox, ok: false, error: message });
    }
    done++;
    onProgress?.(done, targets.length, t.elementId);
  }
  return results;
}

export type StoreLike = {
  width?: number;
  height?: number;
  pages?: Array<{
    children?: Array<Record<string, unknown>>;
    [k: string]: unknown;
  }>;
  [k: string]: unknown;
};

/**
 * Replace image elements marked for vectorize with OpenPolotno svg elements.
 * Shape: { type:'svg', src: data URL or http URL, x,y,width,height, ... }.
 */
export function patchStoreWithSvgs(
  storeJson: StoreLike,
  replacements: Array<{ elementId: string; src: string }>,
): StoreLike {
  const byId = new Map(replacements.map((r) => [r.elementId, r.src]));
  const pages = (storeJson.pages || []).map((page) => {
    const children = (page.children || []).map((el) => {
      const id = String(el.id || '');
      const src = byId.get(id);
      if (!src) return el;
      return {
        ...el,
        type: 'svg',
        src,
        keepRatio: false,
        stretchEnabled: true,
        cropX: 0,
        cropY: 0,
        cropWidth: 1,
        cropHeight: 1,
        custom: {
          ...(typeof el.custom === 'object' && el.custom ? (el.custom as object) : {}),
          vectorize: true,
          vectorized: true,
        },
      };
    });
    return { ...page, children };
  });
  return { ...storeJson, pages };
}

/** Upload SVG markup; returns public /api URL or null on failure. */
export async function uploadSvgAsset(
  templateId: string,
  elementId: string,
  svg: string,
): Promise<string | null> {
  try {
    const res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(templateId)}/assets`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: `orn-${elementId}`,
        svg,
        contentType: 'image/svg+xml; charset=utf-8',
      }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { url?: string };
    return data.url || null;
  } catch {
    return null;
  }
}

/** Persist updated store JSON for a user template. */
export async function updateTemplateJson(
  templateId: string,
  storeJson: unknown,
): Promise<boolean> {
  try {
    const res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(templateId)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ json: storeJson }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchTemplateJson(templateId: string): Promise<StoreLike | null> {
  try {
    const res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(templateId)}.json`));
    if (!res.ok) return null;
    return (await res.json()) as StoreLike;
  } catch {
    return null;
  }
}
